import React, { useState } from 'react';
import { useLang } from '@shootout-comps/i18n';
import { useAuth, FAQ_DATA, FAQ_TAG_LABELS as TAG_LABELS, FAQ_TAG_COLORS as TAG_COLORS } from '@shootout-comps/core';

export default function FaqPage({ onNavigateToContact }: { onNavigateToContact: () => void }) {
  const { t } = useLang();
  const { isSuperuser } = useAuth();

  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [openIndex, setOpenIndex]       = useState<number | null>(null);
  const [search, setSearch]             = useState('');

  // Build the visible tag list — hide the Admin tab from regular users
  const visibleTags = Object.entries(TAG_LABELS).filter(
    ([key]) => key !== 'admin' || isSuperuser
  );

  const filtered = FAQ_DATA.filter(item => {
    // Hide admin questions entirely from non-superusers
    if (item.tag === 'admin' && !isSuperuser) return false;

    const matchesTag    = activeFilter === 'all' || item.tag === activeFilter;
    const matchesSearch = !search ||
      item.q.toLowerCase().includes(search.toLowerCase()) ||
      (Array.isArray(item.a) ? item.a : [item.a]).some(s => s.toLowerCase().includes(search.toLowerCase()));

    return matchesTag && matchesSearch;
  });

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div style={{ padding: 'var(--page-pad)', maxWidth: '720px', margin: '0 auto', paddingBottom: '4rem' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem, 5vw, 2.2rem)', color: 'var(--gold)', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>
          ❓ {t.navFaq}
        </h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', lineHeight: 1.6 }}>
          {t.faqSubtitle}
        </p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
        <span style={{ position: 'absolute', left: '0.8rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.9rem', color: 'var(--chalk-dim)', pointerEvents: 'none' }}>
          🔍
        </span>
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setOpenIndex(null); }}
          placeholder={t.faqSearch}
          style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.9rem 0.6rem 2.4rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: 'var(--chalk)', fontSize: '0.88rem', outline: 'none' }}
        />
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {visibleTags.map(([key, label]) => {
          const active = activeFilter === key;
          const col    = key === 'all' ? null : TAG_COLORS[key];
          return (
            <button
              key={key}
              onClick={() => { setActiveFilter(key); setOpenIndex(null); }}
              style={{
                padding: '0.3rem 0.8rem', borderRadius: '20px',
                border: `1px solid ${active ? (col?.border ?? 'var(--gold)') : 'rgba(255,255,255,0.12)'}`,
                background: active ? (col?.bg ?? 'rgba(201,168,76,0.12)') : 'transparent',
                color: active ? (col?.color ?? 'var(--gold)') : 'var(--chalk-dim)',
                fontSize: '0.78rem', fontWeight: active ? 600 : 400,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* FAQ items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--chalk-dim)', fontSize: '0.88rem' }}>
            {t.faqNoResults}
          </div>
        )}
        {filtered.map((item, i) => {
          const isOpen   = openIndex === i;
          const tagStyle = item.tag ? TAG_COLORS[item.tag] : null;
          return (
            <div key={i} style={{ background: isOpen ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.025)', border: `1px solid ${isOpen ? 'rgba(201,168,76,0.25)' : 'rgba(255,255,255,0.07)'}`, borderRadius: '10px', overflow: 'hidden', transition: 'border-color 0.2s, background 0.2s' }}>
              <button
                onClick={() => toggle(i)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem 1.1rem', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'start' }}
              >
                {item.tag && tagStyle && (
                  <span style={{ flexShrink: 0, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0.15rem 0.45rem', borderRadius: '3px', background: tagStyle.bg, border: `1px solid ${tagStyle.border}`, color: tagStyle.color }}>
                    {TAG_LABELS[item.tag]?.replace(/^[^\s]+\s/, '') ?? item.tag}
                  </span>
                )}
                <span style={{ flex: 1, fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', fontWeight: 600, color: isOpen ? 'var(--chalk)' : 'var(--chalk-dim)', lineHeight: 1.4, transition: 'color 0.15s' }}>
                  {item.q}
                </span>
                <span style={{ flexShrink: 0, fontSize: '0.75rem', color: isOpen ? 'var(--gold)' : 'var(--chalk-dim)', transition: 'transform 0.2s, color 0.15s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>
                  ▾
                </span>
              </button>

              {isOpen && (
                <div style={{ padding: '0 1.1rem 1.1rem 1.1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {Array.isArray(item.a) ? (
                    <ol style={{ margin: '0.75rem 0 0', paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {item.a.map((para, pi) => (
                        <li key={pi} style={{ color: 'var(--chalk-dim)', fontSize: '0.875rem', lineHeight: 1.65 }}>
                          {para}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p style={{ margin: '0.75rem 0 0', color: 'var(--chalk-dim)', fontSize: '0.875rem', lineHeight: 1.65 }}>
                      {item.a}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <p style={{ marginTop: '2.5rem', textAlign: 'center', color: 'var(--chalk-dim)', fontSize: '0.78rem', lineHeight: 1.6 }}>
        {t.faqContact}{' '}
        <span style={{ color: 'var(--gold)', cursor: 'pointer', textDecoration: 'underline' }} onClick={onNavigateToContact}>
          {t.faqContactLink}
        </span>
        .
      </p>
    </div>
  );
}
